import { UnitCategory } from "../../SensorConfigurations/SensorConfigurationTypes.js";
import { Unit } from "../UnitConfigurations.js";

export type UnitCategoryConfiguration = {
    allowedUnits: Unit[];
    databaseUnit: Unit;
};

export const UnitCategoryConfigurations: Record<UnitCategory, UnitCategoryConfiguration> = {
    liquidFlow: {
        databaseUnit: "liters_per_minute",
        allowedUnits: ["liters_per_minute", "liters_per_hour", "gallons_per_minute", "gallons_per_hour"],
    },
    humidity: {
        databaseUnit: "percent_rh",
        allowedUnits: ["percent_rh"],
    },
    airTemperature: {
        databaseUnit: "celsius",
        allowedUnits: ["celsius", "fahrenheit"],
    },
    plantTemperature: {
        databaseUnit: "celsius",
        allowedUnits: ["celsius", "fahrenheit"],
    },
    soilTemperature: {
        databaseUnit: "celsius",
        allowedUnits: ["celsius", "fahrenheit"],
    },
    liquidPressure: {
        databaseUnit: "kilopascal",
        allowedUnits: ["kilopascal", "psi", "bar"],
    },
    volumetricSoilMoisture: {
        databaseUnit: "percent",
        allowedUnits: ["percent"],
    },
    resistiveSoilMoisture: {
        allowedUnits: ["kilopascal"],
        databaseUnit: "kilopascal",
    },
    soilTension: {
        databaseUnit: "kilopascal",
        allowedUnits: ["kilopascal"],
    },
    soilElectricalConductivity: {
        databaseUnit: "microsiemens_per_cm",
        allowedUnits: ["microsiemens_per_cm", "deciSiemens_per_meter"],
    },
    windSpeed: {
        databaseUnit: "meters_per_second",
        allowedUnits: ["meters_per_second", "kilometer_per_hour", "miles_per_hour"],
    },
    windHeading: {
        databaseUnit: "degree",
        allowedUnits: ["degree"],
    },
    soilVolumetricIonContent: {
        databaseUnit: "percent_vic",
        allowedUnits: ["percent_vic"],
    },
    precipitation: {
        databaseUnit: "millimeter",
        allowedUnits: ["millimeter", "inch"],
    },
    liquidTankLevel: {
        databaseUnit: "percent",
        allowedUnits: ["percent"],
    },
    sapFlow: {
        databaseUnit: "liters_per_hour",
        allowedUnits: ["liters_per_hour", "gallons_per_hour"],
    },
    stemWaterPotential: {
        databaseUnit: "kilopascal",
        allowedUnits: ["kilopascal", "psi", "bar"],
    },
    electricalVoltage: {
        databaseUnit: "volt",
        allowedUnits: ["volt"],
    },
    evapotranspiration: {
        databaseUnit: "millimeter",
        allowedUnits: ["millimeter", "inch"],
    },
    electricalPulseCount: {
        databaseUnit: "pulse",
        allowedUnits: ["pulse"],
    },
    flowmeterKFactor: {
        databaseUnit: "liters_per_pulse",
        allowedUnits: ["liters_per_pulse", "gallons_per_pulse"],
    },
    flowmeterOffset: {
        databaseUnit: "hertz",
        allowedUnits: ["pulse_per_minute"],
    },
    flowmeterMinimumFrequency: {
        databaseUnit: "hertz",
        allowedUnits: ["pulse_per_minute"],
    },
    pressureTransducerSlope: {
        databaseUnit: "kilopascal_per_volt",
        allowedUnits: ["kilopascal_per_volt"],
    },
    dimensionless: {
        databaseUnit: "dimensionless",
        allowedUnits: ["dimensionless"],
    },
    arbitraryPercentage: {
        databaseUnit: "dimensionless",
        allowedUnits: ["dimensionless"],
    },
    ndvi: {
        databaseUnit: "ndvi",
        allowedUnits: ["ndvi"],
    },
    ndviVariance: {
        databaseUnit: "ndvi",
        allowedUnits: ["ndvi"],
    },
    stemRadiusChange: {
        databaseUnit: "micrometer",
        allowedUnits: ["micrometer"],
    },
    solarRadiation: {
        databaseUnit: "watt_per_square_meter",
        allowedUnits: ["watt_per_square_meter"],
    },
    depth: {
        databaseUnit: "meter",
        allowedUnits: ["meter", "centimeter", "foot", "inch"],
    },
} as const satisfies Record<UnitCategory, UnitCategoryConfiguration>;

/**
 * Runtime type guard for `unitCategory` values arriving over the wire (typed loosely as `string`).
 * The keys of `UnitCategoryConfigurations` are the authoritative known set. Uses `hasOwnProperty`
 * so inherited prototype keys (e.g. "toString") are not mistaken for known categories.
 */
export function isKnownUnitCategory(value: string): value is UnitCategory {
    return Object.prototype.hasOwnProperty.call(UnitCategoryConfigurations, value);
}

/** Safe accessor: returns the configuration for a known category, or `undefined` for an unknown one. */
export function getUnitCategoryConfiguration(value: string): UnitCategoryConfiguration | undefined {
    return isKnownUnitCategory(value) ? UnitCategoryConfigurations[value] : undefined;
}
